import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  TextField,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  RiseOutlined as MarketingIcon,
  CloseOutlined as CloseIcon,
  PhoneOutlined as PhoneIcon,
  FileTextOutlined as TranscriptIcon,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import HttpService from "../services/httpService";

const BRAND_GRADIENT = "linear-gradient(135deg, #1C375D 0%, #2a5ea8 100%)";
const PLAY_BTN_SX = {
  color: "#1C375D",
  backgroundColor: "#eaf1fb",
  transition: "transform .15s ease, background-color .15s ease",
  "&:hover": { backgroundColor: "#d9e6f7", transform: "scale(1.08)" },
};

// New, standalone "Move to Marketing" flow driven off Runo call data - not
// the older "Move to Marketing" option still reachable via "Move Deal" ->
// stage dropdown, and doesn't touch that flow in any way. The button that
// opens this dialog is only enabled once the lead is Runo-eligible (see
// isMoveToMarketingRunoDisabled in manageProjectLog/index.js).
//
// Submitting hits PUT /lead/moveToMarketingWithRunoData, which forwards the
// reason + Runo call data to an outside classifier and moves the lead to
// Junk or reverts it based on the verdict. The outside classifier's URL and
// response contract are still placeholders on the backend (see
// routes/lead.js) pending the real API - nothing here needs to change once
// that's wired up.
// Matches constants.moveToMarketingReasons on the server; the server rejects
// this reason without a date, so the two must agree on the id.
const PROJECT_TIMELINE_REASON_ID = "project_timeline";

// Today, so a date in the past cannot be picked - the lead would come back due
// the moment it was saved.
const todayISO = () => new Date().toISOString().split("T")[0];

const MoveToMarketingRunoDialog = ({
  open,
  onClose,
  leadId,
  reasonOptions = [],
  eligibility,
  onSubmitSuccess,
}) => {
  const [selectedReasonId, setSelectedReasonId] = useState("");
  // "Project Timeline" means the customer named a date to be approached
  // again. That date is the only thing that can bring the lead back out of
  // Marketing, so for this reason it is collected and required.
  const [holdRevokeDate, setHoldRevokeDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const {
    unansweredCount = 0,
    transcribedCallCount = 0,
    latestCall,
    latestTranscribedCall,
    calls = [],
    // The customer's number isn't dialable, so this lead got here without any
    // call history at all - see routes/runo.js: /moveToMarketingEligibility.
    phoneInvalid = false,
    phoneInvalidReason = "",
  } = eligibility || {};

  // With an unusable number the only honest reason is "No Response / Invalid /
  // Switched Off / Unreachable" - every other reason describes something a
  // salesperson learned ON a call that, here, never happened. It's also the
  // only reason the backend re-validates the number for: it junks the lead
  // instead of spending a doomed AI Bot call on it.
  const INVALID_NUMBER_REASON_ID = "no_response";

  // `transcription` is a Mixed field on the Runo model - only use it when it
  // actually holds text, so an object never lands in the transcripts array.
  const transcriptTextOf = (call) => {
    const text = call?.transcript || (typeof call?.transcription === "string" ? call.transcription : "");
    return typeof text === "string" && text.trim() ? text.trim() : null;
  };

  const resetState = () => {
    setSelectedReasonId("");
    setHoldRevokeDate("");
    setError("");
  };

  // Guards against a stale reason selection if the eligibility data (and
  // its transcribedCallCount) changes while the dialog is still open - in
  // either direction: "No Response" becomes invalid once a transcript shows
  // up, and every other reason becomes invalid if the transcript count
  // drops back to zero.
  useEffect(() => {
    // An invalid number pins the reason regardless of call history - it wins
    // over both rules below, since a number that can't be dialled makes every
    // call-derived reason moot.
    if (phoneInvalid) {
      if (selectedReasonId && selectedReasonId !== INVALID_NUMBER_REASON_ID) {
        setSelectedReasonId("");
      }
      return;
    }
    if (selectedReasonId === "no_response" && transcribedCallCount > 0) {
      setSelectedReasonId("");
    } else if (selectedReasonId && selectedReasonId !== "no_response" && transcribedCallCount === 0) {
      setSelectedReasonId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribedCallCount, phoneInvalid]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
      resetState();
      onClose();
    }
  };

  const formatCallDate = (value) => {
    if (!value) return "N/A";
    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      return "N/A";
    }
  };

  const handleSubmit = async () => {
    if (!selectedReasonId) {
      setError("Please select a reason");
      return;
    }
    if (selectedReasonId === PROJECT_TIMELINE_REASON_ID && !holdRevokeDate) {
      setError("Pick the date the customer asked to be approached again");
      return;
    }
    const selectedReason = reasonOptions.find((r) => r._id === selectedReasonId);
    setSubmitting(true);
    setError("");
    try {
      // Every call's transcript and recording, newest call first (the
      // eligibility API already sorts by startTime desc), so the classifier
      // reviews the whole conversation history rather than a single call.
      const callHistory = Array.isArray(calls) ? calls : [];
      const transcripts = callHistory.map(transcriptTextOf).filter(Boolean);
      const recordingUrls = callHistory.map((call) => call?.recordingUrl).filter(Boolean);

      // The singular `transcript`/`recordingUrl` keys are kept alongside the
      // new arrays - the classifier contract already reads them, so dropping
      // them here would break it.
      const payload = {
        leadId,
        reason: selectedReason || selectedReasonId,
        // Only sent for Project Timeline; the server ignores it otherwise and
        // requires it when that reason is chosen.
        holdRevokeDate: selectedReasonId === PROJECT_TIMELINE_REASON_ID ? holdRevokeDate : null,
        transcript: latestTranscribedCall?.transcript || latestCall?.transcript || null,
        recordingUrl: latestTranscribedCall?.recordingUrl || latestCall?.recordingUrl || null,
        transcripts,
        recordingUrls,
        runoCallDetails: {
          unansweredCount,
          transcribedCallCount,
          totalCallCount: callHistory.length,
          latestCall,
          latestTranscribedCall,
          calls: callHistory,
        },
      };
      const response = await HttpService.submitMoveToMarketingWithRunoData(payload);
      if (response?.data?.success) {
        // The backend's own `message` (a sibling of `data`, not nested in
        // it) already describes exactly which of the 3 outcomes happened -
        // pass it along so the caller shows the real result instead of
        // re-deriving one from `isJunk` alone (which the "No Response"
        // outcome doesn't even set, since it skips the classifier).
        if (onSubmitSuccess) onSubmitSuccess({ ...response.data.data, message: response.data.message });
        resetState();
        onClose();
      } else {
        setError(response?.data?.message || "Failed to move the lead to marketing");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to move the lead to marketing");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, minWidth: { xs: "90%", sm: "500px" }, overflow: "hidden" } }}
    >
      <Box sx={{ background: BRAND_GRADIENT, color: "#fff", px: 3, pt: 2.5, pb: 2, position: "relative" }}>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={submitting}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "rgba(255,255,255,0.85)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
          }}
        >
          <CloseIcon style={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.16)",
              flexShrink: 0,
            }}
          >
            <MarketingIcon style={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontSize="1.05rem" fontWeight={700} sx={{ color: "#fff" }}>
              Move to Marketing
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Based on this lead's Runo call history
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 1, bgcolor: "#fafbfd" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: "0.875rem", borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Without this the dialog just shows "0 Unanswered / 0 Transcribed"
            and gives no hint why the button was enabled at all - state the
            actual reason plainly, and what submitting will do. */}
        {phoneInvalid && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: "0.8rem", borderRadius: 2 }}>
            <strong>This customer's number is not dialable</strong>
            {phoneInvalidReason ? ` (${phoneInvalidReason})` : ""} - no calls could be placed, which
            is why there is no call history below. Submitting will mark this lead as Junk; no AI Bot
            call will be attempted.
          </Alert>
        )}

        {submitting && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              bgcolor: "#eaf1fb",
              border: "1px solid #c9dcf5",
            }}
          >
            <CircularProgress size={22} thickness={4.5} sx={{ color: "#1C375D" }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: "#1C375D" }}>
                Analyzing call history…
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reviewing the reason and Runo call data - this can take a few seconds.
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ border: "1px solid #e6e9f0", borderRadius: 3, bgcolor: "#fff", p: 2, mb: 2, opacity: submitting ? 0.6 : 1, transition: "opacity .2s ease" }}>
          <Typography
            variant="subtitle2"
            sx={{ fontSize: "0.78rem", color: "text.secondary", fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <PhoneIcon style={{ fontSize: 13 }} />
            Runo Call Summary
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, mb: latestCall ? 1.5 : 0 }}>
            <Box sx={{ flex: 1, textAlign: "center", bgcolor: "#f7f9fc", borderRadius: 2, py: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: "#1C375D" }}>
                {unansweredCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">Unanswered Calls</Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: "center", bgcolor: "#f7f9fc", borderRadius: 2, py: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: "#1C375D" }}>
                {transcribedCallCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">Transcribed Calls</Typography>
            </Box>
          </Box>

          {latestCall && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                Most recent call: {formatCallDate(latestCall.startTime)} ({latestCall.type || "N/A"})
                {latestCall.tag ? ` · Status: ${latestCall.tag}` : ""}
              </Typography>
              {latestCall.recordingUrl && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                  <Tooltip title={isPlaying ? "Pause recording" : "Play recording"}>
                    <IconButton size="small" onClick={togglePlay} sx={PLAY_BTN_SX}>
                      {isPlaying ? <PauseCircleOutlined style={{ fontSize: 15 }} /> : <PlayCircleOutlined style={{ fontSize: 15 }} />}
                    </IconButton>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    {isPlaying ? "Playing recording…" : "Listen to recording"}
                  </Typography>
                  <audio
                    ref={audioRef}
                    src={latestCall.recordingUrl}
                    preload="none"
                    onEnded={() => setIsPlaying(false)}
                    style={{ display: "none" }}
                  />
                </Box>
              )}
              {/* Only this call's OWN transcript belongs here - falling back
                  to latestTranscribedCall's transcript would show up under a
                  call (e.g. an unanswered one) that never actually had a
                  transcript, which reads as a contradiction. */}
              {latestCall.transcript && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <TranscriptIcon style={{ fontSize: 13, color: "#5a6b85", marginTop: 2 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                  >
                    {latestCall.transcript}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Shown as its own, separately-labelled block only when the
              latest transcribed call is a DIFFERENT call from the one above
              - makes it clear this transcript belongs to an earlier call,
              not the most recent (possibly unanswered) one. */}
          {latestTranscribedCall?.transcript && latestTranscribedCall?.callId !== latestCall?.callId && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                Most recent transcribed call: {formatCallDate(latestTranscribedCall.startTime)} ({latestTranscribedCall.type || "N/A"})
                {latestTranscribedCall.tag ? ` · Status: ${latestTranscribedCall.tag}` : ""}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <TranscriptIcon style={{ fontSize: 13, color: "#5a6b85", marginTop: 2 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                  {latestTranscribedCall.transcript}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <TextField
          select
          label="Reason"
          size="small"
          fullWidth
          disabled={submitting}
          value={selectedReasonId}
          onChange={(e) => {
            setSelectedReasonId(e.target.value);
            setError("");
          }}
          sx={{ mb: 2, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
        >
          {reasonOptions.map((reason) => {
            const isNoResponseReason = reason._id === "no_response";
            // An unusable number leaves exactly one valid reason, whatever the
            // call history says - checked first so it overrides both rules
            // below rather than competing with them.
            const disableForInvalidPhone = phoneInvalid && !isNoResponseReason;
            // A transcribed call means the customer did pick up and speak,
            // so "No Response / Invalid / Switched Off / Unreachable" can't
            // be the right reason for this lead - disable it instead of
            // letting the user pick a reason the call history contradicts.
            const disableNoResponse = !phoneInvalid && isNoResponseReason && transcribedCallCount > 0;
            // The reverse case: with zero transcribed calls there's no call
            // content for the outside classifier to review, so every OTHER
            // reason (which all route through that classifier) is
            // meaningless here - only "No Response" (which skips the
            // classifier and re-triggers an AI Bot call instead) applies.
            const disableNoTranscript = !phoneInvalid && !isNoResponseReason && transcribedCallCount === 0;
            const disabled = disableForInvalidPhone || disableNoResponse || disableNoTranscript;
            const tooltipTitle = disableForInvalidPhone
              ? "This customer's number is not dialable, so only \"No Response / Invalid\" can be selected"
              : disableNoResponse
                ? "This lead has a transcribed call, so it can't be marked as No Response / Invalid"
                : disableNoTranscript
                  ? "This lead has no transcribed call yet - only \"No Response\" can be selected until a call is transcribed"
                  : "";
            const menuItem = (
              <MenuItem value={reason._id} disabled={disabled}>
                {reason.name}
              </MenuItem>
            );
            return disabled ? (
              <Tooltip key={reason._id} title={tooltipTitle} placement="right">
                <span>{menuItem}</span>
              </Tooltip>
            ) : (
              React.cloneElement(menuItem, { key: reason._id })
            );
          })}
        </TextField>

        {selectedReasonId === PROJECT_TIMELINE_REASON_ID && (
          <TextField
            label="Approach the customer again on"
            type="date"
            value={holdRevokeDate}
            onChange={(e) => setHoldRevokeDate(e.target.value)}
            disabled={submitting}
            required
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: todayISO() }}
            error={Boolean(error) && !holdRevokeDate}
            helperText="The date the customer asked to be contacted again. An AI call is placed automatically on this date."
            sx={{ mt: 2 }}
          />
        )}

        <Alert severity="info" sx={{ fontSize: "0.8rem", borderRadius: 2 }}>
          {phoneInvalid
            ? "The number will be re-checked on submit. If it is still not dialable the lead is marked Junk straight away - no AI review and no Bot call, since neither can reach this customer."
            : "This reason and the Runo call details above will be sent for review. The lead will be marked Junk if confirmed, or left as-is otherwise."}
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, bgcolor: "#fafbfd" }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={submitting}
          size="small"
          sx={{
            color: "#5a6b85",
            borderColor: "#d8dee8",
            "&:hover": { borderColor: "#a9b4c6", bgcolor: "rgba(90,107,133,0.06)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !selectedReasonId}
          size="small"
          sx={{
            minWidth: 140,
            borderRadius: 2.5,
            background: BRAND_GRADIENT,
            boxShadow: "none",
            "&:hover": { background: "linear-gradient(135deg, #16294a 0%, #234f8f 100%)", boxShadow: "0 4px 12px rgba(28,55,93,0.25)" },
            "&.Mui-disabled": { background: "#d8dee8", color: "#fff" },
          }}
        >
          {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Move to Marketing"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveToMarketingRunoDialog;
